// Block registry. Each block lists its tiles + gameplay flags.
// solid: collides; opaque: hides neighbour faces; cutout: alpha-tested;
// cross: drawn as an X sprite; liquid: 'water' | 'lava'.

export const B = {};
export const BLOCKS = [];

function def(name, opts) {
  const id = BLOCKS.length;
  const t = opts.tex;
  const tex = typeof t === 'string' ? { top: t, side: t, bottom: t } : { top: t.top, side: t.side, bottom: t.bottom ?? t.top };
  const b = {
    id,
    name,
    shape: opts.shape ?? null,
    climb: opts.climb ?? false,
    tex,
    solid: opts.solid ?? true,
    opaque: opts.opaque ?? true,
    cutout: opts.cutout ?? false,
    cross: opts.cross ?? false,
    liquid: opts.liquid ?? null,
    slippery: opts.slippery ?? false,
    bounce: opts.bounce ?? 0,
    speed: opts.speed ?? 0,
    hurt: opts.hurt ?? false,
    step: opts.step ?? 'stone', // footstep sound family
    color: opts.color ?? '#888', // particle colour
  };
  BLOCKS.push(b);
  B[name.toUpperCase()] = id;
  return id;
}

def('air', { tex: 'stone', solid: false, opaque: false });
def('grass', { tex: { top: 'grass_top', side: 'grass_side', bottom: 'dirt' }, step: 'grass', color: '#5fa63a' });
def('dirt', { tex: 'dirt', step: 'grass', color: '#7a5233' });
def('stone', { tex: 'stone', color: '#808084' });
def('cobble', { tex: 'cobble', color: '#76767a' });
def('planks', { tex: 'planks', step: 'wood', color: '#b88a4f' });
def('dark_planks', { tex: 'dark_planks', step: 'wood', color: '#6e4e30' });
def('log', { tex: { top: 'log_top', side: 'log_side' }, step: 'wood', color: '#6b4a2a' });
def('leaves', { tex: 'leaves', opaque: false, cutout: true, step: 'grass', color: '#3f8f2c' });
def('birch_log', { tex: { top: 'birch_top', side: 'birch_side' }, step: 'wood', color: '#e0ddd0' });
def('birch_leaves', { tex: 'birch_leaves', opaque: false, cutout: true, step: 'grass', color: '#6aa846' });
def('pine_log', { tex: { top: 'pine_top', side: 'pine_side' }, step: 'wood', color: '#4d3520' });
def('pine_leaves', { tex: 'pine_leaves', opaque: false, cutout: true, step: 'grass', color: '#2f6b3a' });
def('snow_pine_leaves', { tex: 'snow_pine_leaves', opaque: false, cutout: true, step: 'snow', color: '#dfe9f2' });
def('sand', { tex: 'sand', step: 'sand', color: '#e0d09a' });
def('red_sand', { tex: 'red_sand', step: 'sand', color: '#c06a3a' });
def('sandstone', { tex: { top: 'sandstone_top', side: 'sandstone_side' }, step: 'stone', color: '#dccb8f' });
def('chiseled_sandstone', { tex: { top: 'sandstone_top', side: 'chiseled_sandstone' }, color: '#dccb8f' });
def('cactus', { tex: { top: 'cactus_top', side: 'cactus_side' }, step: 'wood', color: '#3e8d34' });
def('snow', { tex: 'snow', step: 'snow', color: '#f2f6fc' });
def('snow_grass', { tex: { top: 'snow', side: 'snow_side', bottom: 'dirt' }, step: 'snow', color: '#eef3fa' });
def('ice', { tex: 'ice', slippery: true, step: 'glass', color: '#a8d8ff' });
def('packed_ice', { tex: 'packed_ice', slippery: true, step: 'glass', color: '#8cc4f0' });
def('gold_block', { tex: 'gold_block', step: 'metal', color: '#f5c542' });
def('diamond_block', { tex: 'diamond_block', step: 'metal', color: '#4fe0e0' });
def('emerald_block', { tex: 'emerald_block', step: 'metal', color: '#3cc870' });
def('ruby_block', { tex: 'ruby_block', step: 'metal', color: '#d83048' });
def('brick', { tex: 'brick', color: '#a8473a' });
def('stone_brick', { tex: 'stone_brick', color: '#808084' });
def('glass', { tex: 'glass', opaque: false, cutout: true, step: 'glass', color: '#dff4ff' });
def('glowstone', { tex: 'glowstone', step: 'glass', color: '#ffd36b' });
def('lamp', { tex: 'lamp', step: 'metal', color: '#ffd080' });
def('obsidian', { tex: 'obsidian', color: '#2a1a40' });
def('magma_rock', { tex: 'magma_rock', color: '#7a2f2a' });
def('magma', { tex: 'magma', color: '#ff8a20' });
def('basalt', { tex: { top: 'stone', side: 'basalt' }, color: '#3d3d45' });
def('crystal', { tex: 'crystal', step: 'glass', color: '#b06cff' });
def('glitch', { tex: 'glitch', step: 'metal', color: '#ff2bd6' });
def('void_stone', { tex: 'void_stone', color: '#e9e7b8' });
def('void_brick', { tex: 'void_brick', color: '#a77bca' });
def('coal_ore', { tex: 'coal_ore', color: '#3a3a3a' });
def('iron_ore', { tex: 'iron_ore', color: '#d8b090' });
def('gold_ore', { tex: 'gold_ore', color: '#f5d040' });
def('diamond_ore', { tex: 'diamond_ore', color: '#60f0f0' });
def('ruby_ore', { tex: 'ruby_ore', color: '#e83050' });
def('bounce', { tex: { top: 'bounce', side: 'bounce_side', bottom: 'bounce_side' }, bounce: 22, step: 'slime', color: '#7be35a' });
def('speed', { tex: { top: 'speed', side: 'neon_black' }, speed: 1, step: 'metal', color: '#ffd228' });
def('marble', { tex: 'marble', color: '#ece8e2' });
def('path', { tex: { top: 'path', side: 'dirt' }, step: 'grass', color: '#a07c50' });
def('pumpkin', { tex: { top: 'pumpkin_top', side: 'pumpkin_side' }, step: 'wood', color: '#e48820' });
def('jack_o', { tex: { top: 'pumpkin_top', side: 'jack_face' }, step: 'wood', color: '#e48820' });
def('lucky', { tex: 'lucky', color: '#ffcc33' });
def('checkpoint', { tex: { top: 'checkpoint', side: 'stone_brick' }, step: 'metal', color: '#78e678' });
def('cloud', { tex: 'cloud', step: 'snow', color: '#ffffff' });
def('bookshelf', { tex: { top: 'planks', side: 'bookshelf' }, step: 'wood', color: '#b88a4f' });
for (const c of ['red', 'orange', 'yellow', 'lime', 'cyan', 'blue', 'purple', 'pink', 'white', 'black'])
  def('neon_' + c, { tex: 'neon_' + c, step: 'metal', color: '#ccc' });
def('water', { tex: 'stone', solid: false, opaque: false, liquid: 'water', color: '#4a90e0' });
def('lava', { tex: 'stone', solid: false, opaque: false, liquid: 'lava', hurt: true, color: '#ff8a20' });
def('flower_red', { tex: 'flower_red', solid: false, opaque: false, cross: true, color: '#e03030' });
def('flower_yellow', { tex: 'flower_yellow', solid: false, opaque: false, cross: true, color: '#f0d030' });
def('flower_blue', { tex: 'flower_blue', solid: false, opaque: false, cross: true, color: '#5096fa' });
def('tall_grass', { tex: 'tall_grass', solid: false, opaque: false, cross: true, color: '#5fa63a' });
def('dead_bush', { tex: 'dead_bush', solid: false, opaque: false, cross: true, color: '#866030' });
def('mushroom', { tex: 'mushroom', solid: false, opaque: false, cross: true, color: '#d03030' });
def('crystal_shard', { tex: 'crystal_shard', solid: false, opaque: false, cross: true, color: '#b070ff' });
def('snow_bush', { tex: 'snow_bush', solid: false, opaque: false, cross: true, color: '#e8f0f8' });
def('fire_flower', { tex: 'fire_flower', solid: false, opaque: false, cross: true, color: '#ff8020' });

// Neon colours used for particles
const NEON_HEX = { red: '#ec4040', orange: '#ff8c28', yellow: '#ffd632', lime: '#78dc3c', cyan: '#28d2e6', blue: '#3c6ef0', purple: '#a050f0', pink: '#ff64be', white: '#eceef4', black: '#2a2c34' };
for (const [k, v] of Object.entries(NEON_HEX)) BLOCKS[B['NEON_' + k.toUpperCase()]].color = v;

export const isSolid = (id) => BLOCKS[id].solid;
export const isOpaque = (id) => BLOCKS[id].opaque;

// Append new IDs: old saved block numbers must keep their meaning.
def('crafting_table', { tex: { top: 'craft_top', side: 'craft_side', bottom: 'planks' }, step: 'wood', color: '#b8804c' });

// Working blocks. Shapes are local AABBs shared by the renderer and collision code.
const box = (a,b,c,d,e,f) => [a,b,c,d,e,f];
def('door', {tex:'planks', opaque:false, shape:[box(0,0,.4,1,1,.6)], step:'wood'});
def('door_open', {tex:'planks', solid:false, opaque:false, shape:[box(0,0,0,.15,1,1)], step:'wood'});
def('chest', {tex:{top:'chest_top',side:'chest_front'}, opaque:false, shape:[box(.06,0,.06,.94,.8,.94)], step:'wood'});
def('furnace', {tex:{top:'stone',side:'furnace'}, color:'#677380'});
def('bed', {tex:'bed', opaque:false, shape:[box(0,0,0,1,.45,1)], step:'wood'});
def('sign_board', {tex:'planks', solid:false, opaque:false, shape:[box(.1,.4,.4,.9,.9,.6),box(.45,0,.45,.55,.4,.55)], step:'wood'});
def('slab', {tex:'planks', opaque:false, shape:[box(0,0,0,1,.5,1)], step:'wood'});
def('stairs', {tex:'planks', opaque:false, shape:[box(0,0,0,1,.5,1),box(0,.5,.5,1,1,1)], step:'wood'});
def('fence', {tex:'planks', opaque:false, shape:[box(.4,0,.4,.6,1,.6),box(0,.35,.44,1,.5,.56),box(0,.75,.44,1,.9,.56)], step:'wood'});
def('ladder', {tex:'planks', solid:false, opaque:false, climb:true, shape:[box(.15,0,.8,.25,1,.9),box(.75,0,.8,.85,1,.9),...[.1,.4,.7].map(y=>box(.15,y,.8,.85,y+.1,.9))], step:'wood'});
def('torch', {tex:'lamp', solid:false, opaque:false, shape:[box(.4,0,.4,.6,.8,.6)], color:'#ffb85b'});
def('rail', {tex:'rail', solid:false, opaque:false, shape:[box(0,0,0,1,.08,1)], step:'metal'});
def('powered_rail', {tex:'powered_rail', solid:false, opaque:false, shape:[box(0,0,0,1,.08,1)], step:'metal'});
def('wire', {tex:'neon_red', solid:false, opaque:false, shape:[box(.38,0,0,.62,.06,1),box(0,0,.38,1,.06,.62)]});
def('switch', {tex:'stone', solid:false, opaque:false, shape:[box(.2,0,.2,.8,.15,.8),box(.44,.15,.44,.56,.5,.56)]});
def('circuit_lamp', {tex:'lamp', color:'#ffcb62'});
def('drawbridge', {tex:'dark_planks', color:'#8b5c3c', step:'wood'});
def('drawbridge_open', {tex:'dark_planks',solid:false,opaque:false,shape:[box(0,0,.92,1,1,1)],step:'wood'});
def('farm_plot', {tex:'dirt',opaque:false,shape:[box(0,0,0,1,.25,1)],step:'grass'});
def('perch', {tex:{top:'gold_block',side:'log_side'},color:'#d5a250',step:'wood'});
def('enchant_table', {tex:{top:'crystal',side:'bookshelf'},color:'#b884e9'});
def('portal_frame', {tex:'obsidian', color:'#9a67e5'});
